import type { OrganizationData } from './organizationService';
import { paymentService } from './paymentService';
import { ANALYTICS_EVENTS, trackEventAsync } from './firebaseAnalytics';
import { PROFILE_ENTERPRISE_BILLING_PATH } from '../constants/billingProfilePaths';
import { isTransientBillingRelatedAxiosError } from '../utils/billingPolicyFetchErrors';

export {
  PROFILE_BILLING_PATH,
  PROFILE_ENTERPRISE_BILLING_PATH,
  billingProfileManagementPath,
  normalizeBillingProfilePathname,
  type BillingProfileManagementPath,
} from '../constants/billingProfilePaths';

export type BillingEntitlementSurface = 'post_login_redirect' | 'protected_route';

/** Network / server errors while fetching subscription — do not treat as "no plan" for transient handling. */
export const isTransientBillingEntitlementFailure = isTransientBillingRelatedAxiosError;

export type BillingPricingPath = '/profile/pricing' | typeof PROFILE_ENTERPRISE_BILLING_PATH;

/**
 * Routes where missing subscription must not force a redirect to pricing (checkout flows and
 * profile/account settings). `/profile/integrations` stays gated by RequireBasePlanRoute.
 */
export function isSubscriptionOptionalRoute(pathname: string): boolean {
  if (pathname === '/checkout' || pathname === '/return') return true;
  if (pathname === '/profile' || pathname.startsWith('/profile/')) {
    return pathname !== '/profile/integrations';
  }
  return false;
}

/** Stripe checkout + post-payment return — do not redirect into onboarding/terms until the user leaves this flow. */
export function isPaymentFlowRoute(pathname: string): boolean {
  return pathname === '/checkout' || pathname === '/return';
}

export function pricingPathForOrganization(
  organization: OrganizationData | null,
): BillingPricingPath {
  return organization ? PROFILE_ENTERPRISE_BILLING_PATH : '/profile/pricing';
}

/**
 * Org lookup used by billing/onboarding guards.
 * This is intentionally a pass-through: retry ownership belongs to the caller.
 * - React Query callers should rely on query retry configuration.
 * - Non-React-Query callers must wrap their fetch function with retry behavior.
 */
export async function fetchOrganizationForBillingGate(
  fetchOrganization: () => Promise<OrganizationData | null>,
): Promise<OrganizationData | null> {
  return fetchOrganization();
}

export type SubscriptionProbeResult =
  | { status: 'active' }
  | { status: 'inactive' }
  | { status: 'transient'; error: unknown }
  | { status: 'fail_closed'; error: unknown };

/**
 * Subscription probe with transient vs fail-closed classification.
 */
export async function probeActiveSubscription(
  organization: OrganizationData | null,
): Promise<SubscriptionProbeResult> {
  try {
    const hasSubscription = organization
      ? !!(await paymentService.getEnterpriseCurrentSubscription())
      : !!(await paymentService.getCurrentSubscription());
    return hasSubscription ? { status: 'active' } : { status: 'inactive' };
  } catch (error) {
    if (isTransientBillingEntitlementFailure(error)) {
      return { status: 'transient', error };
    }
    return { status: 'fail_closed', error };
  }
}

type BillingEntitlementMetricParams =
  | {
      surface: BillingEntitlementSurface;
      organizationOutcome: 'fetch_exhausted';
      error: unknown;
    }
  | {
      surface: BillingEntitlementSurface;
      organizationOutcome: 'success';
      organization: OrganizationData | null;
      subscription: SubscriptionProbeResult;
    };

/**
 * One structured event per billing-gate attempt so post-login and ProtectedRoute stay comparable in analytics.
 */
export function recordBillingEntitlementMetric(params: BillingEntitlementMetricParams): void {
  if (params.organizationOutcome === 'fetch_exhausted') {
    void trackEventAsync(ANALYTICS_EVENTS.BILLING_ENTITLEMENT_RESOLVED, {
      surface: params.surface,
      organization_outcome: 'fetch_exhausted',
      subscription_outcome: 'skipped',
      has_organization: false,
    });
    return;
  }

  const { subscription, organization, surface } = params;
  const subscription_outcome =
    subscription.status === 'active'
      ? 'active'
      : subscription.status === 'inactive'
        ? 'inactive'
        : subscription.status === 'transient'
          ? 'transient'
          : 'fail_closed';

  void trackEventAsync(ANALYTICS_EVENTS.BILLING_ENTITLEMENT_RESOLVED, {
    surface,
    organization_outcome: 'success',
    subscription_outcome,
    has_organization: !!organization,
  });
}
